import './JobsPage.scss'

import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import ApplyModal from "./modals/ApplyModal";
import JobPostingModal from "./modals/JobPostingModal";
import { useCurrentUser } from '../../UserProvider';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faLocationDot, faFilter, faStar } from "@fortawesome/free-solid-svg-icons";
import LocationInput from '../../components/Form/LocationInput';
import { Link } from 'react-router-dom';
import { GENRES, INSTRUMENTS } from "../../constants";
import classNames from 'classnames';

import Slider from "rc-slider";
import "rc-slider/assets/index.css";

export default function JobsPage() {
  const { currentUser } = useCurrentUser();

  const [jobs, setJobs]                       = useState([]);
  const [selectedJob, setSelectedJob]         = useState(null);
  const [jobToEdit, setJobToEdit]             = useState(null);
  const [page, setPage]                       = useState(1);
  const [hasMore, setHasMore]                 = useState(true);
  const [displayFilters, setDisplayFilters]   = useState(false);
  const [loading, setLoading]                 = useState(true);
  const perPage = 10;

  const [filters, setFilters] = useState({
    query:       "",
    location:    "",
    latitude:    null,
    longitude:   null,
    distance:    25,
    genres:      [],
    instruments: []
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const toggleGenre = (genre) => {
    setFilters(f => {
      const newGenres = f.genres.includes(genre) ? f.genres.filter(g => g !== genre) : [...f.genres, genre];
      return { ...f, genres: newGenres };
    });
  };

  const toggleInstrument = (instrument) => {
    setFilters(f => {
      const newInst = f.instruments.includes(instrument) ? f.instruments.filter(i => i !== instrument) : [...f.instruments, instrument];
      return { ...f, instruments: newInst };
    });
  };

  const handleDistanceChange = (value) => {
    setFilters(f => ({ ...f, distance: value }));
  };

  const clearLocationValues = () => {
    setFilters(f => ({ ...f, location: "", latitude: null, longitude: null }));
  };

  const handleLocationInputClear = () => {
    clearLocationValues();
  };

  const handleLocationPlaceChange = (place) => {
    if (!place || !place.address || !place.latitude || !place.longitude) {
      return clearLocationValues();
    }

    setFilters(f => ({
      ...f,
      location: place.address,
      latitude: place.latitude,
      longitude: place.longitude
    }));
  };

  // Debounce filter changes (BUT do NOT clear jobs on first mount)
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      // Only clear results if the user actually typed a query or changed a filter
      if (filters.query.trim() !== debouncedFilters.query) {
        setJobs([]);
        setHasMore(true);
        setLoading(true);
      }
      setDebouncedFilters(filters);
    }, 800);

    return () => clearTimeout(handler);
  }, [filters, debouncedFilters]);

  // Fetch jobs on load and whenever debounced filters or page changes
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const payload = {
          query:       debouncedFilters.query,
          location:    debouncedFilters.location,
          latitude:    debouncedFilters.latitude,
          longitude:   debouncedFilters.longitude,
          distance:    debouncedFilters.distance,
          genres:      debouncedFilters.genres,
          instruments: debouncedFilters.instruments,
          per_page:    perPage,
          page:        page,
        };

        const { data } = await api.post(`/job_postings/search`, payload);
        setLoading(false);

        if (page === 1) {
          setJobs(data);
        } else {
          setJobs(prev => [...prev, ...data]);
        }

        if (data.length < perPage) {
          setHasMore(false);
        }

      } catch (err) {
        console.error('Error fetching jobs:', err);
      }
    };

    fetchJobs();
  }, [debouncedFilters, page]);

  const downloadAll = async () => {
    const payload = {
      query:    "",
      per_page: 500,
      page:     1,
    };

    const { data } = await api.post(`/job_postings/search`, payload);

    if (!Array.isArray(data) || data.length === 0) {
      alert("No data found.");
      return;
    }

    const headers = Object.keys(data[0]);

    const csvRows = [
      headers.join(","),
      ...data.map(item =>
        headers
          .map(header => {
            let val = item[header];
            if (val === null || val === undefined) val = "";
            val = String(val).replace(/"/g, '""');
            return `"${val}"`;
          })
          .join(",")
      ),
    ];

    const csvString = csvRows.join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.setAttribute("href", url);
    link.setAttribute("download", "job_postings.csv");
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log("CSV downloaded!");
    console.log(data);
  };

  const reIndex = async () => {
    await api.post(`/job_postings/embed`);
    alert('Done indexing job postings');
  };

  return (
    <div className='__jobs'>
      <div className='content'>
        <div className='header'>
          <div className='primary'>
            Find all job opportunities in the music industry in one place, at your finger tips.
          </div>
          <div className='secondary'>
            All job opportunities in one place. Find the right one for you.
          </div>
        </div>

        {currentUser?.is_admin &&
          <div className='admin-buttons'>
            <button className='add-new ppy-btn ppy-btn-primary' onClick={reIndex}>
              Index Job Postings
            </button>
            <button className='add-new ppy-btn ppy-btn-primary' onClick={() => setJobToEdit(undefined)}>
              Add New Job Posting
            </button>
            <button className='add-new ppy-btn ppy-btn-primary' onClick={() => downloadAll()}>
              Download All
            </button>
          </div>
        }

        <div className='main'>
          {displayFilters &&
            <div className='filters'>
              <div className='filters-header'>
                Filters
                <button className='btn-close' onClick={() => setDisplayFilters(false)} />
              </div>

              <label>Address</label>
              <LocationInput
                className="location"
                name="location"
                placeholder="Enter location"
                value={ filters.location }
                onInputClear={ handleLocationInputClear }
                onPlaceChange={ handleLocationPlaceChange }
              />

              <label>Range</label>
              <Slider
                min={5}
                max={100}
                value={ filters.distance }
                onChange={handleDistanceChange}
                disabled={!filters.location.trim()}
              />
              <div className='mileage'><label>{ filters.distance } miles</label></div>

              <label>Instruments</label>
              <div className="genre-grid">
                { INSTRUMENTS.map((instrument) => (
                  <div
                    key={ instrument }
                    className={ classNames("genre", { "selected": filters.instruments.includes(instrument) }) }
                    onClick={ () => toggleInstrument(instrument) }
                  >
                    { instrument }
                  </div>
                )) }
              </div>

              <label>Genre</label>
              <div className="genre-grid">
                { GENRES.map((genre) => (
                  <div
                    key={ genre }
                    className={ classNames("genre", { "selected": filters.genres.includes(genre) }) }
                    onClick={ () => toggleGenre(genre) }
                  >
                    { genre }
                  </div>
                )) }
              </div>

              <button className='mobile-filter ppy-btn ppy-btn-primary' onClick={() => setDisplayFilters(false) }>
                Filter <FontAwesomeIcon icon={faFilter} />
              </button>
            </div>
          }
          <div className='results'>
            <div className='controls'>
              <button className='filter-toggle' onClick={() => setDisplayFilters(!displayFilters) }>
                <FontAwesomeIcon icon={faFilter} />
              </button>
              <input
                className='search'
                type="text"
                placeholder="Filter jobs by typing here..."
                value={filters.query}
                onChange={(e) => setFilters(f => ({ ...f, query: e.target.value }))}
              />
            </div>

            <div className='bottom-section'>
              <div className='search-results'>
                {!loading && jobs.length === 0 &&
                  <div className='empty-state'>
                    <div className='no-results'>No results found</div> 
                    <img src="/images/guy.svg" alt="Guy" />
                  </div>
                }

                {jobs.map((job) => (
                  <div key={job.id} className={ classNames("job-card", { "recommended": job.is_recommended }) }>
                    {job.is_recommended &&
                      <div className='papaya-recommends'>
                        <span><FontAwesomeIcon icon={faStar} /> Papaya Recommends</span>
                      </div>
                    }
                    <div className='job-header'>
                      <Link to={`/jobs/${job.id}`} className='job-title'>
                        {job.title}
                      </Link>
                      {currentUser?.is_admin &&
                        <button className='edit ppy-btn ppy-btn-primary' onClick={() => setJobToEdit(job)}>
                          <FontAwesomeIcon icon={faPencil} />
                        </button>
                      }
                    </div>

                    {job.tags && job.tags.trim() !== '' && (
                      <div className='tags'>
                        {job.tags.split(',').map((tag, idx) => (
                          <div key={idx} className='tag'>
                            {tag.trim()}
                          </div>
                        ))}
                      </div>
                    )}

                    {!!job.budget && job.budget !== 0 && (
                      <div className='budget'>Budget: ${job.budget}</div>
                    )}

                    <div className='body'>{job.description}</div>
                    <div className='location'><FontAwesomeIcon icon={faLocationDot} /> {job.location}</div>
                    <div className='apply'>
                      <button className="ppy-btn ppy-btn-primary" onClick={() => setSelectedJob(job)}>
                        Apply Now
                      </button>
                    </div>
                    <div className="dates">
                      <div>Posted {new Date(job.created_at).toLocaleDateString('en-US')}</div>
                      { job.expires_at &&
                        <div className="expires-at">
                          <div>Expires {new Date(job.expires_at).toLocaleDateString('en-US')}</div>
                          {new Date(job.expires_at) < new Date() && (
                            <div className="expired">Expired</div>
                          )}
                        </div>
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {hasMore && jobs.length > 0 && (
              <div className="load-more">
                <button
                  className="ppy-btn ppy-btn-primary"
                  onClick={() => setPage(prev => prev + 1)}
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </div>

        <ApplyModal
          show={!!selectedJob}
          handleClose={() => setSelectedJob(null)}  
          job={selectedJob}
        />

        <JobPostingModal
          show={jobToEdit !== null}
          handleClose={() => setJobToEdit(null)}
          job={jobToEdit}
          setJobs={setJobs}
        />
      </div>
    </div>
  );
}
